import React, { useState, useEffect } from 'react';
import {
  isIOS,
  isNativeApp,
  isPWA,
  shouldShowInstallPrompt,
  dismissInstallPrompt,
  incrementPromptShowCount,
} from '../../utils/pwa';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { IconButton } from '../ui/IconButton';

// BeforeInstallPromptEvent is not in standard TypeScript DOM lib
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;

export const InstallPrompt: React.FC = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOSDevice, setIsIOSDevice] = useState(false);

  useEffect(() => {
    if (isNativeApp() || isPWA() || !shouldShowInstallPrompt()) {
      return;
    }

    const isIOSUser = isIOS();
    setIsIOSDevice(isIOSUser);

    if (isIOSUser) {
      const timer = setTimeout(() => {
        setShowPrompt(true);
        incrementPromptShowCount();
      }, 3000);
      return () => clearTimeout(timer);
    }

    const handleBeforeInstall = (e: Event) => {
      e.preventDefault();
      deferredPrompt = e as BeforeInstallPromptEvent;
      setShowPrompt(true);
      incrementPromptShowCount();
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    const handleAppInstalled = () => {
      setShowPrompt(false);
      deferredPrompt = null;
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      setShowPrompt(false);
    }
    deferredPrompt = null;
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    dismissInstallPrompt();
  };

  if (!showPrompt) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[1000] animate-slide-up-full motion-reduce:animate-none p-3 sm:p-4 pb-[calc(12px+env(safe-area-inset-bottom))] sm:pb-[calc(16px+env(safe-area-inset-bottom))]">
      <Card tone="elevated" variant="bordered" padding="none" className="relative shadow-xl max-w-[400px] mx-auto dark:border-border-medium p-5 sm:p-6">
        <IconButton
          icon={
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M15 5L5 15M5 5L15 15"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          }
          aria-label="Close install prompt"
          variant="ghost"
          size="md"
          onClick={handleDismiss}
          className="absolute top-3 right-3"
        />

        <div className="flex items-center justify-center w-14 h-14 bg-primary-light rounded-lg mb-4 text-accent">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
            <path
              d="M12 2L12 14M12 14L8 10M12 14L16 10"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M4 17V19C4 20.1046 4.89543 21 6 21H18C19.1046 21 20 21.1046 20 19V17"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        <h3 className="text-xl font-semibold text-text-primary m-0 mb-2 tracking-tight">Install Helm</h3>
        <p className="text-sm text-text-secondary m-0 mb-5 leading-relaxed">
          {isIOSDevice
            ? 'Add Helm to your home screen for the best experience.'
            : 'Install for quick access and offline support.'}
        </p>

        {isIOSDevice ? (
          <div className="flex flex-col gap-3 mb-4">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center w-6 h-6 min-w-[24px] bg-accent text-text-inverse rounded-full text-xs font-semibold">1</span>
              <span className="text-sm text-text-secondary flex items-center gap-1.5">
                Tap the Share button
                <svg
                  className="text-accent shrink-0"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path d="M12 2L12 14M12 2L8 6M12 2L16 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M4 12V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </span>
            </div>
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center w-6 h-6 min-w-[24px] bg-accent text-text-inverse rounded-full text-xs font-semibold">2</span>
              <span className="text-sm text-text-secondary">Scroll and tap "Add to Home Screen"</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center w-6 h-6 min-w-[24px] bg-accent text-text-inverse rounded-full text-xs font-semibold">3</span>
              <span className="text-sm text-text-secondary">Tap "Add" to confirm</span>
            </div>
          </div>
        ) : (
          <div className="flex gap-3 flex-col sm:flex-row">
            <Button variant="primary" fullWidth onClick={handleInstall}>Install</Button>
            <Button variant="secondary" fullWidth onClick={handleDismiss}>Not now</Button>
          </div>
        )}

        {isIOSDevice && (
          <Button variant="text" className="block w-full p-3 text-center" onClick={handleDismiss}>
            Maybe later
          </Button>
        )}
      </Card>
    </div>
  );
};
